
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { NavigationGroup } from './navigationItems';

interface SidebarDropdownGroupProps {
  group: NavigationGroup;
  className?: string;
  activeColorClass?: string;
}

const SidebarDropdownGroup: React.FC<SidebarDropdownGroupProps> = ({ 
  group, 
  className = '', 
  activeColorClass = 'text-blue-600' 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile } = useSidebar();
  const isCollapsed = state === 'collapsed' && !isMobile;
  const [isOpen, setIsOpen] = useState(false);

  // Check if the entire group is muted (all items are non-MVP)
  const isGroupMuted = group.mvpActive === false || group.items.every(item => item.mvpActive === false);

  const isActiveRoute = (route: string) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const hasActiveChild = group.items.some(item => isActiveRoute(item.route) && item.mvpActive !== false);

  useEffect(() => {
    if (hasActiveChild) {
      setIsOpen(true);
    }
  }, [hasActiveChild]);

  const handleItemClick = (item: typeof group.items[0]) => {
    if (item.mvpActive === false) {
      navigate(`/coming-soon?feature=${encodeURIComponent(item.title)}`);
    } else {
      navigate(item.route);
    }
  };

  if (group.items.length === 0) return null;

  return (
    <SidebarGroup className={className}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            className={`
              h-9 transition-colors w-full
              ${isGroupMuted ? 'opacity-40' : ''}
              ${hasActiveChild
                ? 'bg-accent text-accent-foreground font-medium'
                : 'hover:bg-accent/50'
              }
              ${isCollapsed ? 'justify-center px-0' : 'justify-between px-3'}
            `}
            tooltip={isCollapsed ? group.title : undefined}
          >
            <div className="flex items-center min-w-0 flex-1">
              <group.icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-3 flex-shrink-0'}`} />
              {!isCollapsed && (
                <span className="text-sm truncate">
                  {group.title}
                </span>
              )}
            </div>
            {!isCollapsed && isGroupMuted && (
              <Badge variant="outline" className="text-[9px] px-1 py-0 mr-1 border-muted-foreground/30 text-muted-foreground/60">
                Soon
              </Badge>
            )}
            {!isCollapsed && (
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </SidebarMenuButton>
        </CollapsibleTrigger>

        {!isCollapsed && (
          <CollapsibleContent className="ml-3 mt-0.5 space-y-0.5">
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const isMuted = item.mvpActive === false;
                  return (
                    <SidebarMenuItem key={item.route}>
                      <SidebarMenuButton
                        isActive={!isMuted && isActiveRoute(item.route)}
                        onClick={() => handleItemClick(item)}
                        className={`
                          h-8 transition-colors
                          ${isMuted ? 'opacity-40' : ''}
                          ${!isMuted && isActiveRoute(item.route)
                            ? 'bg-accent text-accent-foreground font-medium'
                            : 'hover:bg-accent/50'
                          }
                          justify-start px-3
                        `}
                      >
                        <item.icon className="w-3.5 h-3.5 mr-3 flex-shrink-0" />
                        <span className="text-sm truncate flex-1">
                          {item.title}
                        </span>
                        {isMuted && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto border-muted-foreground/30 text-muted-foreground/60">
                            Soon
                          </Badge>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        )}
      </Collapsible>
    </SidebarGroup>
  );
};

export default SidebarDropdownGroup;
